/* eslint-disable no-nested-ternary */
import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Label } from 'reactstrap';
import { ChevronDown, ChevronUp, Cross, InfoCircle } from '@saleshandy/icons';
import { Button, ImageIcon, Pills } from '@saleshandy/design-system';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import insertTextAtCursor from 'insert-text-at-cursor';
import { debounce } from 'lodash';
import { PlusBlue } from '../../../../../../../shared/svg/plus-blue';
import EditBlue from '../../../../../../../shared/svg/edit-blue';
import { Images } from '../../../../../../../shared/app-constants';
import Select from '../../../../../../../shared/design-system/components/select';
import InlineAutosuggest, {
  InlineAutosuggestItem,
} from '../../../../../../../shared/design-system/components/molecules/inline-autosuggest/inline-autosuggest';
import {
  AI_ICONS,
  OutputTypeLabels,
  validateMergeTags,
  validatePrompt,
  formatOptions,
} from '../../../utils/helper';
import TemplateFooter from '../../template-footer';
import TemplateHeader from '../../template-header';
import { ColumnSuggestion, EnrichOwnPromptProps } from '../types';
import TemplateDetailSkeleton from './template-detail-skeleton';
import OverlayMergeTags from '../../../../../../../shared/components/overlay-merge-tags/overlay-merge-tags';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../../../shared/design-system/components/overlay';
import { ExceptionTypes } from '../../../../../../../shared/types/exception-types';
import {
  br2nl,
  isValidHTML,
  nl2br,
} from '../../../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-merge-tag-mapping-with-values';
import { constructFilterGroupFromPayload } from '../../../../prospect-filters-modal/utils/construct-filter-group-from-payload';
import { prepareMergeTagOptions } from '../../../../../../sequence/shared/modals/email-modal/helpers';
import { applyProspectFiltersFromAPI } from '../../../../../helpers/apply-prospect-filters';
import { RootState } from '../../../../../../../store/root-reducer';
import Help from '../../../../../../../shared/svg/help';
import {
  AiColumnsActionType,
  SupportedAiModelsOutput,
  SupportedAiModelsOutputColumnType,
  AiColEnrichmentModalSteps,
} from '../../../enums';
import { executeOnRequestStatus } from '../../../../../../../shared/utils';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import DiscardUnsavedChangesModal from '../../discard-unsaved-changes-modal';
import { RequestStatus } from '../../../../../../../shared/enums/request-status';
import Switch, {
  Size,
} from '../../../../../../../shared/design-system/components/atoms/switch';
import Editor from '../../../../../../../shared/editor';
import toaster from '../../../../../../../shared/toaster';
import Globes from '../../../../../../../shared/svg/globe';
import ExistingPromptModal from '../../existing-prompt-modal';
import AiCreditCapsule from '../../ai-credit-capsule/ai-credit-capsule';
import TruncatedTextWithAutoTooltip from '../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import { IsDirtyProps } from '../../../type';
import {
  insertChipForMergeTag,
  stripChipTagsFromHtml,
} from '../../../../../../../shared/editor/helpers';
import { convertHtmlToText } from '../../../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';
import './_run-settings.scss';
import ProspectFiltersModal from '../../../../prospect-filters-modal';
import { FilterGroup } from '../../../../prospect-filters-modal/types';
import { getDefaultFilterGroupObject } from '../../../../prospect-filters-modal/utils/get-default-filter-group-object';
import { deepEqual } from '../../../../../../../shared/functions/deep-equal';

const AiColTemplateDetail: React.FC<EnrichOwnPromptProps> = memo(
  ({
    enrichProspect = true,
    handleModalClose,
    handleBack,
    stepData,
    currentStep,
    handleMoveToNextStep,
    fields,
    prospectFiltersFieldsList,
    selectedAiTemplateShareableId,
    getTemplateByIdRequestStatus,
    getTemplateById,
    aiColumnTemplateRegenerateResponse,
    getTemplateConfigResponseStatus,
    saveAndDontRunStatus,
    getAiModelList,
    selectedProspectRowIds,
    filter,
    deSelectedProspectRowId,
    bulkProspectSelected,
    isProspectEnrichmentEnabledMeta,
    aiCreditSingleCreditPrice,
    setSelectedAiTemplateShareableIds,
    fetchAiModel,
    fetchContactFields,
    fetchTemplateById,
    saveAndRun,
    resetTemplateConfigResponse,
    enablePollingRequest,
    fetchFields,
    resetBulkProspectSelected,
    resetTemplateById,
    isAiColumnModalVisible,
    resetAiColumnTemplateRegenerateResponse,
    resetGetTemplateByIdRequest,
    fetchPostLoadMetaRequest,
    autoRunConditionFilterGroups,
    setAutoRunConditionFilterGroups,
    setAutoRunConditionFilterGroupsLogic,
    resetAutoRunConditionFilterGroups,
    setIsAiColEnrichmentFromModal,
  }) => {
    useLocation();
    const dispatch = useDispatch();
    const appliedProspectEnrichmentFilters = useSelector(
      (state: RootState) => state.prospect.appliedProspectEnrichmentFilters,
    );
    const stepDataShareableId = stepData?.TEMPLATE_DETAIL?.shareableId;
    const templateIdFromUrl = useMemo(() => {
      // Prefer stepData/Redux; fallback to URL param for legacy deep linking
      const queryParams = new URLSearchParams(window.location.search);
      const fromUrl = queryParams.get('templateId');
      const finalId = stepDataShareableId || fromUrl;
      if (finalId) {
        setSelectedAiTemplateShareableIds(finalId);
        return finalId;
      }
      return null;
    }, [stepDataShareableId]);

    const editorRef = useRef<any>(null);
    const contentRef = useRef<any>();
    const cancelAiColTemplateRef = useRef<any>(null);
    const columnNameInputRef = useRef<HTMLInputElement>(null);
    const currentTemplateIdRef = useRef<string | null>(null);
    const inMailEditorRef = useRef<any>(null);

    const [enrichPromptData, setEnrichPromptData] = useState({
      columnName: '',
      aiModelId: '1',
      fieldId: '', // Add fieldId to track selected field
      selectedOutputFormat: SupportedAiModelsOutput.Default,
      instructions: '',
      mergeTags: [],
      isWebSearchEnabled: false,
      selectedType: SupportedAiModelsOutputColumnType.PlainText,
      customOutputFormatInput: '',
      isNewColumnName: true,
      isAutoRunEnabled: false,
      autoRunConditions: null,
    });
    const [customOutputFormat, setCustomOutputFormat] = useState(false);
    const [isCreditsPopoverOpen, setIsCreditsPopoverOpen] = useState(false);
    const [isTextPopoverOpen, setIsTextPopoverOpen] = useState(false);
    const [isRunSettingsPopoverOpen, setIsRunSettingsPopoverOpen] = useState(
      false,
    );
    const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
    const [error, setError] = useState({
      message: '',
      error: false,
      columnError: '',
    });
    const [isDirty, setIsDirty] = useState(false);
    const [originalData, setOriginalData] = useState<IsDirtyProps>(null);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [isMergeTagDropdownOpen, setIsMergeTagDropdownOpen] = useState(false);
    const [showPromptModal, setShowPromptModal] = useState(false);
    // State variable for selected field in EXISTING_COLUMN step - updates on column change
    const [selectedField, setSelectedField] = useState(null);
    // Run Settings state
    const [
      showRunSettingsConditionModal,
      setShowRunSettingsConditionModal,
    ] = useState(false);
    const regex = /\{\{[^}]+\}\}/g;

    // Map field types to column types for EXISTING_COLUMN step
    const getColumnTypeFromFieldType = (fieldType: string) => {
      switch (fieldType) {
        case 'text':
          return SupportedAiModelsOutputColumnType.PlainText;
        case 'long-text':
          return SupportedAiModelsOutputColumnType.LongText;
        case 'number':
          return SupportedAiModelsOutputColumnType.Number;
        default:
          return SupportedAiModelsOutputColumnType.PlainText;
      }
    };

    // Helper function to convert autoRunConditions and dispatch filter group actions
    const handleAutoRunConditionsUpdateReduxStateFromAPI = useCallback(
      (autoRunConditions: any) => {
        if (
          autoRunConditions &&
          prospectFiltersFieldsList &&
          prospectFiltersFieldsList.length > 0
        ) {
          try {
            const filterGroups = constructFilterGroupFromPayload({
              filterGroups: autoRunConditions,
              fields: prospectFiltersFieldsList,
            });

            setAutoRunConditionFilterGroups(filterGroups);
            setAutoRunConditionFilterGroupsLogic(
              autoRunConditions?.group_logic || 'AND',
            );
          } catch (conversionError) {
            resetAutoRunConditionFilterGroups();
            setAutoRunConditionFilterGroupsLogic('AND');
          }
        } else {
          resetAutoRunConditionFilterGroups();
          setAutoRunConditionFilterGroupsLogic('AND');
        }
      },
      [
        prospectFiltersFieldsList,
        setAutoRunConditionFilterGroups,
        setAutoRunConditionFilterGroupsLogic,
      ],
    );

    const isAutoRunConditionApplied = useMemo(
      () =>
        JSON.stringify(autoRunConditionFilterGroups) !==
        JSON.stringify([getDefaultFilterGroupObject(1)]),
      [autoRunConditionFilterGroups],
    );

    // const [
    //   isInstructionsDirtyForOtherSteps,
    //   setIsInstructionsDirtyForOtherSteps,
    // ] = useState(false); // For EXISTING_COLUMN and CUSTOM_PROMPT steps

    // state to hide tooltip on scroll
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

    // hide tooltip on scroll (only inside .transfer-overlay)
    // useEffect(() => {
    //   function handleScroll(e: Event) {
    //     if ((e.target as HTMLElement)?.closest('.transfer-overlay')) {
    //       setActiveTooltip(null);
    //     }
    //   }
    //   window.addEventListener('scroll', handleScroll, true);
    //   return () => window.removeEventListener('scroll', handleScroll, true);
    // }, []);

    // Apply prefilled fieldId when coming from outside via stepData
    useEffect(() => {
      const existingColumnData =
        stepData?.[AiColEnrichmentModalSteps.EXISTING_COLUMN];
      if (
        currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN &&
        existingColumnData?.fieldId
      ) {
        // Find the field to get its fieldType
        const foundField = fields?.find(
          (field) => String(field.id) === existingColumnData.fieldId,
        );
        setSelectedField(foundField);
        setEnrichPromptData((prev) => ({
          ...prev,
          isNewColumnName: false,
          fieldId: existingColumnData?.fieldId,
          selectedType: getColumnTypeFromFieldType(foundField?.fieldType),
        }));
        fetchPostLoadMetaRequest();
      }
    }, [currentStep, stepData, fields, enrichPromptData?.isNewColumnName]);

    // Compute header title based on current step
    const headerTitle =
      currentStep === AiColEnrichmentModalSteps.CUSTOM_PROMPT
        ? 'Enrich with Your Own Prompt'
        : currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN
        ? 'AI Prospect Enrichment'
        : getTemplateById?.name;

    // Only show title skeleton when viewing template detail
    const isHeaderTitleRequestPending =
      currentStep === AiColEnrichmentModalSteps.TEMPLATE_DETAIL &&
      getTemplateByIdRequestStatus === RequestStatus.Pending;

    const openDiscardModal = () => setIsDiscardModalOpen(true);
    const closeDiscardModal = () => setIsDiscardModalOpen(false);

    // Memoized values
    const allowedMergeTags = useMemo(
      () => new Set(fields?.map((item) => item?.label)),
      [fields],
    );

    const validationErrors = useMemo(
      () => validateMergeTags(enrichPromptData.instructions, allowedMergeTags),
      [enrichPromptData.instructions, allowedMergeTags],
    );

    const mergeTagOptions = useMemo(() => prepareMergeTagOptions(fields), [
      fields,
    ]);

    const debouncedValidate = useMemo(
      () =>
        debounce((instructions, validationError, MergeTagRegex, cb) => {
          const convertHTMLToText = convertHtmlToText(
            stripChipTagsFromHtml(instructions),
          );
          const result = validatePrompt(
            convertHTMLToText,
            validationError,
            MergeTagRegex,
          );
          cb(result);
        }, 300),
      [],
    );

    // Usage
    useEffect(() => {
      if (!enrichPromptData?.instructions) return;

      debouncedValidate(
        enrichPromptData.instructions,
        validationErrors.errors,
        regex,
        ({ errors, errorMessages }) => {
          // set state here instead of returning
          setError((prev) => ({
            ...prev,
            error: errors,
            message: errorMessages,
          }));
        },
      );
    }, [enrichPromptData?.instructions, validationErrors, debouncedValidate]);

    // Create dynamic dropdown options from fields (excluding Email field)
    const fieldOptions = useMemo(
      () =>
        fields
          ?.filter(
            (field) =>
              field?.label !== 'Email' &&
              field?.label !== 'First Name' &&
              field?.label !== 'Last Name' &&
              field?.label !== 'Phone Number',
          )
          ?.map((field) => ({
            key: String(field?.id), // Use field ID as key
            label: field?.label,
            value: String(field?.id), // Convert to string to match expected type
            // Note: isAiGenerated property exists in runtime but not in type definition
            isAiGenerated: (field as any)?.isAiGenerated || false,
            fieldType: field?.fieldType,
          })) || [],
      [fields],
    );

    // Map field options to suggestion items
    const columnTags = useMemo<ColumnSuggestion[]>(
      () =>
        fieldOptions
          ?.filter(
            (f) =>
              !['currency', 'date', 'dropdown']?.includes(
                (f as any)?.fieldType,
              ),
          )
          ?.map((f) => ({
            id: Number(f?.key),
            name: f?.label,
            fieldType: (f as any)?.fieldType,
            isAiGenerated: f?.isAiGenerated,
          })),
      [fieldOptions],
    );

    const closeExistingPromptModal = () => setShowPromptModal(false);

    const AiOptionList = useMemo(
      () =>
        getAiModelList
          ?.filter(
            (item) =>
              // keep only active models
              item?.status !== '0' &&
              // and if web search is enabled, include "perplexity"
              !(
                !enrichPromptData?.isWebSearchEnabled &&
                item?.serviceProvider?.toLowerCase() === 'perplexity'
              ),
          )
          ?.map((item) => ({
            key: String(item?.id),
            label: item?.model,
            value: item?.model,
            icon: AI_ICONS[item?.serviceProvider],
            description: item?.description,
            webEnabled: item.toolCalls.includes('web_search_context'),
            isRecommended: item?.isRecommended,
            creditUsagePerRow: item?.creditUsagePerRow,
          })) || [],
      [getAiModelList, enrichPromptData?.isWebSearchEnabled],
    );

    const getAiModelCreditUsagePerRow = useCallback(
      (modalId) => {
        const selectedAiModelAnalysisIdentifier =
          getAiModelList?.find((item) => item?.id === Number(modalId))
            ?.analysisIdentifier || null;

        if (!selectedAiModelAnalysisIdentifier) return null;
        // const isCustomOutputFormat =
        //   enrichPromptData?.selectedOutputFormat ===
        //   SupportedAiModelsOutput.Custom;

        const decodedAnalysisIdentifier =
          JSON.parse?.(atob(selectedAiModelAnalysisIdentifier)) || null;
        if (decodedAnalysisIdentifier) {
          // Static cost from backend for 250 tokens
          const modalCostWithoutWebSearch =
            decodedAnalysisIdentifier?.t250_noweb_cost || 0;
          const modalCostWithWebSearch =
            decodedAnalysisIdentifier?.t250_web_cost || 0;

          const requiredCredits = (
            (enrichPromptData?.isWebSearchEnabled
              ? modalCostWithWebSearch
              : modalCostWithoutWebSearch) / aiCreditSingleCreditPrice
          ).toFixed(2);

          return requiredCredits.toString();
          // const inputTokens =
          //   getTokenCalculation(
          //     enrichPromptData?.instructions?.length ||
          //       0 +
          //         (isCustomOutputFormat
          //           ? enrichPromptData?.customOutputFormatInput?.length || 0
          //           : 0),
          //   ) ?? 0;

          // const outputFormat = isCustomOutputFormat
          //   ? enrichPromptData?.selectedType
          //   : enrichPromptData?.selectedOutputFormat;
          // const outputTokens = getTokenCalculation(
          //   OutputFormatApproxCharLength?.[outputFormat] ?? 0,
          // );

          // const requiredCredits = getCreditCostForRequestPerRow(
          //   aiCreditSingleCreditPrice,
          //   inputTokens,
          //   outputTokens,
          //   enrichPromptData?.isWebSearchEnabled,
          //   decodedAnalysisIdentifier,
          // );
          // return requiredCredits.toString();
        }
        return null; // Fallback to null
      },
      [
        aiCreditSingleCreditPrice,
        getAiModelList,
        enrichPromptData?.isWebSearchEnabled,
        enrichPromptData?.aiModelId,
        enrichPromptData?.instructions,
        enrichPromptData?.selectedOutputFormat,
        enrichPromptData?.customOutputFormatInput,
        customOutputFormat,
      ],
    );

    const selectedModalAiCreditUsagePerRow = useMemo(
      () => getAiModelCreditUsagePerRow(enrichPromptData?.aiModelId),
      [
        // All below dependencies are used to capture change made by user
        enrichPromptData?.isWebSearchEnabled,
        enrichPromptData?.aiModelId,
        enrichPromptData?.instructions,
        enrichPromptData?.selectedOutputFormat,
        enrichPromptData?.customOutputFormatInput,
        enrichPromptData?.selectedType,
      ],
    );

    const handleWebSearchToggle = () => {
      const selectedAIModelId = enrichPromptData?.aiModelId;
      const isSelectedAIModelIdPerplexity =
        getAiModelList?.find((item) => item?.id === Number(selectedAIModelId))
          ?.serviceProvider === 'perplexity';

      setEnrichPromptData((prev) => ({
        ...prev,
        ...(isSelectedAIModelIdPerplexity && {
          aiModelId: '1',
        }),
        isWebSearchEnabled: !prev?.isWebSearchEnabled,
      }));
    };

    const filterGroup = useMemo(() => {
      const filters = filter?.filterGroupLogic?.filters;
      if (!filters || filters.length === 0) return null;

      return {
        group_logic: filter.filterGroupLogic.group_logic ?? 'AND',
        filters: filters.map((item) => ({
          group: item?.group,
          logic: item?.logic,
          conditions: item?.conditions?.map((logic) => ({
            source: logic?.source,
            field: logic?.field,
            operation: logic?.operation,
            value: logic?.value,
          })),
        })),
      };
    }, [filter]);

    const handleSaveBtnClicked = useCallback(
      (actionType) => {
        if (
          currentStep !== AiColEnrichmentModalSteps.EXISTING_COLUMN
            ? enrichPromptData?.columnName?.trim()?.length === 0
            : enrichPromptData?.isNewColumnName
            ? enrichPromptData?.columnName?.length === 0
            : enrichPromptData?.fieldId?.length === 0
        ) {
          setError((prev) => ({
            ...prev,
            columnError: 'column name should not be empty',
          }));
          return;
        }
        const converrtHTMLToText = convertHtmlToText(
          stripChipTagsFromHtml(enrichPromptData?.instructions),
        );
        const defaultPayload = {
          actionType,
          ...(isDirty ||
          currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN ||
          currentStep === AiColEnrichmentModalSteps.CUSTOM_PROMPT
            ? {}
            : { templateId: getTemplateById?.id }),
          ...(enrichPromptData?.isNewColumnName
            ? { columnName: enrichPromptData?.columnName?.trim() || '' }
            : {}),
          aiModelId: parseInt(enrichPromptData?.aiModelId, 10),
          isWebSearchEnabled: enrichPromptData?.isWebSearchEnabled,
          instructions: br2nl(converrtHTMLToText)
            ?.replace(/<[^>]*>/g, '')
            ?.trim(),
          mergeTags: enrichPromptData?.mergeTags,
          selectedOutputFormat: enrichPromptData?.selectedOutputFormat,
          ...(enrichPromptData?.selectedOutputFormat === 'custom' && {
            customOutputFormatPrompt: enrichPromptData?.customOutputFormatInput,
            ...(!enrichPromptData?.isNewColumnName
              ? {}
              : {
                  customOutputFormatColumnType: enrichPromptData?.selectedType,
                }),
          }),
          ...(enrichPromptData?.isNewColumnName
            ? {}
            : { fieldId: enrichPromptData?.fieldId }),
          isAutoRunEnabled: enrichPromptData?.isAutoRunEnabled,
          ...(enrichPromptData?.autoRunConditions !== null
            ? {
                autoRunConditions: enrichPromptData?.autoRunConditions,
              }
            : {}),
        };
        let extraFields = {};
        switch (actionType) {
          case AiColumnsActionType.SaveAndRunForSelectedRows:
            extraFields = {
              ...(bulkProspectSelected
                ? {
                    ...(deSelectedProspectRowId?.length > 0
                      ? { unSelectedProspectIds: deSelectedProspectRowId }
                      : {}),

                    aiColumnEnrichmentFilters: {
                      ...(filterGroup ? { filterGroup } : {}),
                      sortBy: filter?.sortBy,
                      sortOrder: filter?.sortOrder,
                      ...(filter?.search !== ''
                        ? { search: filter?.search }
                        : {}),
                    },
                  }
                : { prospectIds: selectedProspectRowIds }),
            };
            break;

          case AiColumnsActionType.SaveAndRunForFirst10Rows:
          case AiColumnsActionType.SaveAndRunForEmptyRows:
          case AiColumnsActionType.SaveAndRunForAllRows:
            extraFields = {
              aiColumnEnrichmentFilters: {
                ...(filterGroup ? { filterGroup } : {}),
                sortBy: filter?.sortBy,
                sortOrder: filter?.sortOrder,
                ...(filter?.search !== '' ? { search: filter?.search } : {}),
              },
            };
            break;

          default:
            break;
        }
        const finalPayload = { ...defaultPayload, ...extraFields };
        setIsAiColEnrichmentFromModal(true);
        saveAndRun(finalPayload);
      },
      [
        enrichPromptData,
        isDirty,
        filterGroup,
        bulkProspectSelected,
        selectedProspectRowIds,
        deSelectedProspectRowId,
      ],
    );

    const handleSelectChange = useCallback(
      (field: 'aiModelId' | 'selectedOutputFormat') => (
        selectedOptions: { key: string; label: string; value: string | null }[],
      ) => {
        const option = selectedOptions[0];
        if (option) {
          setEnrichPromptData((prev) => ({ ...prev, [field]: option.key }));
        }
      },
      [],
    );

    const handleTagSelect = useCallback(
      (option: { tag: string }) => {
        if (!option?.tag) return;

        let formattedTag = option.tag;
        const lastPipeIndex = option.tag.lastIndexOf('|');

        if (lastPipeIndex !== -1) {
          const beforePipe = option.tag.slice(0, lastPipeIndex + 1);
          const afterPipe = option.tag.slice(lastPipeIndex + 1);

          formattedTag =
            beforePipe +
            (isValidHTML(afterPipe) ? afterPipe : nl2br(afterPipe));
        }

        editorRef.current?.insertContent(formattedTag);
      },
      [editorRef, isValidHTML, nl2br],
    );

    const handleAddMergeTagInContent = (option) => {
      const { tag } = option;
      const uuid = uuidv4();

      const chipHtml = insertChipForMergeTag(
        tag,
        uuid,
        false,
        true,
        option?.fieldType,
      );

      editorRef.current?.insertContent(chipHtml);
    };

    const renderMergeTagSelection = useCallback(
      () => (
        <OverlayMergeTags
          options={mergeTagOptions}
          onChange={(option) => {
            if (editorRef.current) {
              handleAddMergeTagInContent(option);
              setIsMergeTagDropdownOpen(false);
              return;
            }

            if (inMailEditorRef.current) {
              const { tag } = option;
              const uuid = uuidv4();
              const chipHtml = insertChipForMergeTag(
                tag,
                uuid,
                true,
                true,
                option?.fieldType,
              );
              inMailEditorRef?.current?.insertContent(chipHtml);
              setIsMergeTagDropdownOpen(false);
              return;
            }

            insertTextAtCursor(contentRef?.current, option?.tag);
            setIsMergeTagDropdownOpen(false);
          }}
          placement={Placement.BottomEnd}
          searchPlaceholder="Search Tags"
          className="merge-tags-dropdown"
          onToggle={(show) => setIsMergeTagDropdownOpen(show)}
          showAiGeneratedIcon={true}
        >
          <div
            role="button"
            tabIndex={0}
            className={`enrich-prompt-merge-tag ${
              isMergeTagDropdownOpen ? 'dropdown-open' : ''
            }`}
          >
            <ImageIcon src={Images?.MergeTag} alt="merge-tag" />
          </div>
        </OverlayMergeTags>
      ),
      [mergeTagOptions, handleTagSelect, isMergeTagDropdownOpen],
    );

    const handleMoveBack = useCallback(() => {
      resetTemplateById();
      handleBack?.();
    }, [
      handleBack,
      setAutoRunConditionFilterGroups,
      setAutoRunConditionFilterGroupsLogic,
    ]);

    // Effects: one combined fetch effect with one-time init
    useEffect(() => {
      if (!isProspectEnrichmentEnabledMeta) return;
      if (!getAiModelList || getAiModelList.length === 0) {
        fetchAiModel();
      }
      if (!fields || fields.length === 0) {
        fetchContactFields();
      }

      // Cancel any in-flight template fetch
      if (cancelAiColTemplateRef?.current) {
        cancelAiColTemplateRef?.current?.cancel({
          type: ExceptionTypes.Silent,
          message: 'AI_COLUMN_TEMPLATE_CANCELLED',
        });
        cancelAiColTemplateRef.current = null;
      }
      isAiColumnModalVisible(true);

      // Only fetch template details when in TEMPLATE_DETAIL step
      if (currentStep === AiColEnrichmentModalSteps.TEMPLATE_DETAIL) {
        // Create new cancel token for this cycle
        cancelAiColTemplateRef.current = axios?.CancelToken?.source();

        // Prefer URL param, fallback to Redux state
        const effectiveTemplateId =
          templateIdFromUrl || selectedAiTemplateShareableId;

        // Guard against duplicate fetches
        if (
          effectiveTemplateId &&
          effectiveTemplateId !== currentTemplateIdRef.current
        ) {
          currentTemplateIdRef.current = effectiveTemplateId;
          fetchTemplateById({
            templateId: effectiveTemplateId,
          });
        }
      }

      // Cleanup on dependency change
      // eslint-disable-next-line consistent-return
      return () => {
        if (cancelAiColTemplateRef?.current) {
          cancelAiColTemplateRef?.current?.cancel({
            type: ExceptionTypes.Silent,
            message: 'AI_COLUMN_TEMPLATE_CANCELLED',
          });
          cancelAiColTemplateRef.current = null;
        }
      };
    }, [currentStep, templateIdFromUrl, selectedAiTemplateShareableId]);

    // Close modal if fetching template details fails
    useEffect(() => {
      if (currentStep !== AiColEnrichmentModalSteps.TEMPLATE_DETAIL) return;
      executeOnRequestStatus({
        status: getTemplateByIdRequestStatus,
        onFailed: () => {
          resetGetTemplateByIdRequest();
          handleModalClose();
        },
      });
    }, [getTemplateByIdRequestStatus, currentStep]);

    // Cleanup effect
    useEffect(
      () => () => {
        currentTemplateIdRef.current = null;
      },
      [],
    );

    useEffect(() => {
      if (enrichPromptData.selectedOutputFormat !== 'custom') {
        setEnrichPromptData((prev) => ({
          ...prev,
          selectedType: SupportedAiModelsOutputColumnType.PlainText,
          customOutputFormatInput: '',
        }));
      }
    }, [
      enrichPromptData?.selectedOutputFormat,
      enrichPromptData?.customOutputFormatInput,
    ]);

    useEffect(() => {
      if (aiColumnTemplateRegenerateResponse) {
        setEnrichPromptData((prev) => ({
          ...prev,
          instructions: aiColumnTemplateRegenerateResponse?.instructions,
          mergeTags: validateMergeTags(
            aiColumnTemplateRegenerateResponse?.instructions ||
              prev?.instructions,
            allowedMergeTags,
          ).validTags,
          aiModelId: aiColumnTemplateRegenerateResponse?.shSupportedAiModelId?.toString(),
          isWebSearchEnabled:
            aiColumnTemplateRegenerateResponse?.toolCalls?.includes(
              'web_search_context',
            ) ?? false,
          selectedOutputFormat:
            aiColumnTemplateRegenerateResponse?.selectedOutputFormat,
          customOutputFormatInput:
            aiColumnTemplateRegenerateResponse?.customOutputFormatPrompt || '',
          selectedType: getColumnTypeFromFieldType(selectedField?.fieldType),
          isAutoRunEnabled:
            aiColumnTemplateRegenerateResponse?.isAutoRunEnabled || false,
          autoRunConditions:
            aiColumnTemplateRegenerateResponse?.autoRunConditions,
        }));

        // Convert and dispatch autoRunConditions filter groups
        handleAutoRunConditionsUpdateReduxStateFromAPI(
          aiColumnTemplateRegenerateResponse?.autoRunConditions,
        );

        setIsDirty(false);
        setOriginalData({
          aiModelId:
            aiColumnTemplateRegenerateResponse?.shSupportedAiModelId || '',
          instructions: aiColumnTemplateRegenerateResponse?.instructions || '',
          selectedOutputFormat:
            aiColumnTemplateRegenerateResponse?.selectedOutputFormat,
          isWebSearchEnabled:
            aiColumnTemplateRegenerateResponse?.toolCalls?.length > 0,
          isAutoRunEnabled:
            aiColumnTemplateRegenerateResponse?.isAutoRunEnabled ?? false,
          autoRunConditions:
            aiColumnTemplateRegenerateResponse?.autoRunConditions,
        });
      }

      return () => {
        resetAiColumnTemplateRegenerateResponse();
      };
    }, [aiColumnTemplateRegenerateResponse?.instructions]);

    // Add useEffect to update local state when API data arrives
    useEffect(() => {
      if (getTemplateById && currentTemplateIdRef.current) {
        setEnrichPromptData((prev) => ({
          ...prev,
          columnName: getTemplateById?.columnName || '',
          aiModelId: String(getTemplateById?.suggestedAiModel?.id),
          instructions: nl2br(
            getTemplateById?.userInstructions || prev?.instructions,
          ),
          selectedOutputFormat:
            getTemplateById?.selectedOutputFormat || prev?.selectedOutputFormat,
          isWebSearchEnabled:
            getTemplateById?.toolCalls?.includes('web_search_context') ?? false,
          mergeTags: validateMergeTags(
            getTemplateById?.userInstructions || prev?.instructions,
            allowedMergeTags,
          ).validTags,
          customOutputFormatInput:
            getTemplateById?.customOutputFormatPrompt ||
            prev?.customOutputFormatInput,
          selectedType: selectedField?.fieldType
            ? getColumnTypeFromFieldType(selectedField?.fieldType)
            : getTemplateById?.customOutputFormatColumnType ||
              prev?.selectedType,
          isAutoRunEnabled:
            getTemplateById?.isAutoRunEnabled || prev?.isAutoRunEnabled,
          autoRunConditions:
            getTemplateById?.autoRunConditions || prev?.autoRunConditions,
        }));

        // Convert and dispatch autoRunConditions filter groups
        handleAutoRunConditionsUpdateReduxStateFromAPI(
          getTemplateById?.autoRunConditions ||
            enrichPromptData?.autoRunConditions,
        );

        setOriginalData({
          aiModelId: String(getTemplateById?.suggestedAiModel?.id),
          instructions: nl2br(getTemplateById?.userInstructions),
          selectedOutputFormat: getTemplateById?.selectedOutputFormat,
          isWebSearchEnabled:
            getTemplateById?.toolCalls?.includes('web_search_context') ?? false,
          isAutoRunEnabled: getTemplateById?.isAutoRunEnabled ?? false,
          autoRunConditions: getTemplateById?.autoRunConditions,
        });
      }
    }, [getTemplateById, allowedMergeTags]);

    // Handle template data from template list selection (for EXISTING_COLUMN flow, when user comes after browse template)
    useEffect(() => {
      const existingColumnData =
        stepData?.[AiColEnrichmentModalSteps.EXISTING_COLUMN];
      if (
        (existingColumnData?.fromTemplateList ||
          existingColumnData?.fromOutside) &&
        existingColumnData?.templateId
      ) {
        // Fetch template data when coming from template list
        const effectiveTemplateId = existingColumnData.templateId;
        if (
          effectiveTemplateId &&
          effectiveTemplateId !== currentTemplateIdRef.current
        ) {
          currentTemplateIdRef.current = effectiveTemplateId;
          fetchTemplateById({
            templateId: effectiveTemplateId,
            cancelToken: cancelAiColTemplateRef?.current,
          });
        }
      } else if (
        currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN &&
        !existingColumnData?.fromTemplateList
      ) {
        // Reset editor data when entering EXISTING_COLUMN without template selection

        handleAutoRunConditionsUpdateReduxStateFromAPI(null);
        setEnrichPromptData({
          columnName: '',
          aiModelId: '1',
          fieldId: '',
          selectedOutputFormat: SupportedAiModelsOutput.Default,
          instructions: '',
          mergeTags: [],
          isWebSearchEnabled: false,
          selectedType: SupportedAiModelsOutputColumnType.PlainText,
          customOutputFormatInput: '',
          isNewColumnName: false,
          isAutoRunEnabled: false,
          autoRunConditions: null,
        });
        setOriginalData({
          aiModelId: 1,
          instructions: '',
          selectedOutputFormat: SupportedAiModelsOutput.Default,
          isWebSearchEnabled: false,
          isAutoRunEnabled: false,
          autoRunConditions: null,
        });

        currentTemplateIdRef.current = null;
      }
    }, [stepData, currentStep]);

    useEffect(() => {
      if (!originalData) return;

      const trackedFields: (keyof typeof enrichPromptData)[] = [
        'aiModelId',
        'selectedOutputFormat',
        'instructions',
        'isWebSearchEnabled',
        'isAutoRunEnabled',
        'autoRunConditions',
      ];

      const dirty = trackedFields.some((field) => {
        const currentValue = enrichPromptData[field] as any;
        const originalValue = (originalData as any)[field];

        if (
          typeof currentValue === 'string' ||
          typeof originalValue === 'string'
        ) {
          return (
            (currentValue ?? '').toString().trim() !==
            (originalValue ?? '').toString().trim()
          );
        }

        // Use deep comparison for objects (like autoRunConditions)
        if (
          typeof currentValue === 'object' ||
          typeof originalValue === 'object'
        ) {
          return !deepEqual(currentValue, originalValue);
        }

        return currentValue !== originalValue;
      });

      setIsDirty(dirty);
    }, [enrichPromptData, originalData]);

    // Determine which dirtiness state to use based on current step
    // const shouldUseInstructionsDirty =
    //   (currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN ||
    //     currentStep === AiColEnrichmentModalSteps.CUSTOM_PROMPT) &&
    //   !stepData?.[AiColEnrichmentModalSteps.EXISTING_COLUMN]?.fieldId;

    // const effectiveIsDirty = shouldUseInstructionsDirty
    //   ? isInstructionsDirtyForOtherSteps
    //   : isDirty;

    // Track instructions dirtiness for EXISTING_COLUMN and CUSTOM_PROMPT steps (against empty instructions)
    // useEffect(() => {
    //   if (
    //     currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN ||
    //     currentStep === AiColEnrichmentModalSteps.CUSTOM_PROMPT
    //   ) {
    //     const hasInstructions =
    //       (enrichPromptData.instructions ?? '').toString().trim().length > 0;
    //     setIsInstructionsDirtyForOtherSteps(hasInstructions);
    //   } else {
    //     setIsInstructionsDirtyForOtherSteps(false);
    //   }
    // }, [enrichPromptData.instructions, currentStep]);

    useEffect(() => {
      executeOnRequestStatus({
        status: getTemplateConfigResponseStatus,
        onSuccess: () => {
          resetTemplateConfigResponse();
        },
      });
    }, [getTemplateConfigResponseStatus]);

    useEffect(() => {
      executeOnRequestStatus({
        status: saveAndDontRunStatus,
        onSuccess: () => {
          enablePollingRequest();
          handleModalClose();
          fetchFields({}); // Updating the fields list for column select popup
          if (appliedProspectEnrichmentFilters) {
            applyProspectFiltersFromAPI(
              dispatch,
              appliedProspectEnrichmentFilters,
              filter,
            );
          }
          setEnrichPromptData({
            columnName: '',
            aiModelId: '1',
            fieldId: '',
            selectedOutputFormat: SupportedAiModelsOutput.Default,
            instructions: '',
            mergeTags: [],
            isWebSearchEnabled: false,
            selectedType: SupportedAiModelsOutputColumnType.PlainText,
            customOutputFormatInput: '',
            isNewColumnName: false,
            isAutoRunEnabled: false,
            autoRunConditions: null,
          });
          resetBulkProspectSelected();
          isAiColumnModalVisible(false);
          if (!enrichPromptData.isNewColumnName) {
            toaster.success('AI Column Updated Successfully');
          } else {
            toaster.success('AI Column Created Successfully');
          }
        },
      });
    }, [saveAndDontRunStatus]);

    // Focus column name input when component mounts
    useEffect(() => {
      if (
        currentStep === AiColEnrichmentModalSteps.CUSTOM_PROMPT &&
        columnNameInputRef.current
      ) {
        columnNameInputRef.current.focus();
      }
    }, [currentStep]);

    const handleBrowseTemplateClick = () => {
      if (isDirty) {
        openDiscardModal();
      } else {
        handleMoveToNextStep(AiColEnrichmentModalSteps.TEMPLATE_LIST);
      }
    };

    const handleAutoRunChange = (value: boolean) => {
      setEnrichPromptData((prev) => ({
        ...prev,
        isAutoRunEnabled: value,
      }));
    };

    const handleSetConditionClick = () => {
      setShowRunSettingsConditionModal(true);
    };

    const isColumnLocked = Boolean(
      stepData?.[AiColEnrichmentModalSteps.EXISTING_COLUMN]?.fieldId,
    );
    const lockedLabel = isColumnLocked
      ? fieldOptions.find((fo) => fo.key === enrichPromptData?.fieldId)
          ?.label || ''
      : '';

    return (
      <>
        <TemplateHeader
          title={headerTitle}
          onClose={() => handleModalClose()}
          handleMoveBack={handleMoveBack}
          isCopy
          currentStep={currentStep}
          isTitleRequestPending={isHeaderTitleRequestPending}
          isDirty={isDirty}
        />
        {getTemplateByIdRequestStatus === RequestStatus.Pending ||
        getTemplateConfigResponseStatus === RequestStatus.Pending ? (
          <TemplateDetailSkeleton />
        ) : (
          <div className="enrich-own-prompt-container">
            {/* Your component implementation goes here */}

            <div className="enrich-own-prompt-content-container">
              <div className="enrich-own-prompt-label-container">
                <Label className="enrich-own-prompt-label">
                  Update or Create Target Column
                </Label>
                {enrichProspect && (
                  <OverlayTooltip text="Name your new column here. Each column name must be unique">
                    <InfoCircle className="header-info-circle-icon" />
                  </OverlayTooltip>
                )}
              </div>

              {/* Column Name  */}
              {(() => {
                if (isColumnLocked) {
                  return (
                    <Input
                      type="text"
                      value={lockedLabel}
                      readOnly
                      className="select-column-name-input"
                    />
                  );
                }
                return (
                  <InlineAutosuggest
                    items={columnTags as InlineAutosuggestItem[]}
                    placeholder="Choose existing or type new column name"
                    value={enrichPromptData?.columnName}
                    onChange={(text) => {
                      const q = (text || '').trim().toLowerCase();
                      const hasExact = (columnTags || []).some(
                        (t) => (t.name || '').trim().toLowerCase() === q,
                      );
                      setEnrichPromptData((prev) => ({
                        ...prev,
                        columnName: text,
                        fieldId: q === '' ? '' : prev.fieldId,
                        isNewColumnName:
                          q !== '' && !hasExact ? prev.isNewColumnName : false,
                      }));
                    }}
                    onPickExisting={(item) => {
                      const matched = fieldOptions.find(
                        (fo) => Number(fo.key) === Number(item?.id),
                      );
                      if (!matched) return;
                      if (matched.isAiGenerated) {
                        setShowPromptModal(true);
                      }
                      setSelectedField(
                        (fields || []).find(
                          (f) => String((f as any).id) === matched.key,
                        ) || null,
                      );
                      setEnrichPromptData((prev) => ({
                        ...prev,
                        fieldId: matched.key,
                        selectedType: getColumnTypeFromFieldType(
                          (matched as any)?.fieldType,
                        ),
                        isNewColumnName: false,
                        columnName: matched.label,
                      }));
                    }}
                    onPickNew={(name) => {
                      setEnrichPromptData((prev) => ({
                        ...prev,
                        isNewColumnName: true,
                        fieldId: '',
                        columnName: name,
                      }));
                    }}
                    onClear={() => {
                      setEnrichPromptData((prev) => ({
                        ...prev,
                        columnName: '',
                        fieldId: '',
                        isNewColumnName: false,
                      }));
                      setSelectedField(null);
                      setError((prev) => ({
                        ...prev,
                        columnError: '',
                      }));
                    }}
                    onBlur={() => {
                      const columnName = enrichPromptData?.columnName || '';
                      if (columnName.length > 100) {
                        setError((prev) => ({
                          ...prev,
                          columnError:
                            'Column name should not exceed 100 characters',
                        }));
                      } else {
                        setError((prev) => ({
                          ...prev,
                          columnError: '',
                        }));
                      }
                    }}
                    className={`select-column-name ${
                      error.columnError ? 'auto-suggest-input-error' : ''
                    }`}
                  />
                );
              })()}
              {error.columnError && (
                <div className="error-message">{error.columnError}</div>
              )}
            </div>
            <ExistingPromptModal
              show={showPromptModal}
              onClose={closeExistingPromptModal}
              tempId={Number(enrichPromptData?.fieldId) || 0}
              setEnrichPromptData={setEnrichPromptData}
              setError={setError}
              setOriginalData={setOriginalData}
            />

            <div className="enrich-own-prompt-content-container">
              <div className="enrich-own-prompt-label-container ai-model">
                <Label className="enrich-own-prompt-label">
                  Select AI Model
                </Label>
                <OverlayPopover
                  className="credits-popover"
                  content={
                    <div className="credits-popover-content">
                      <div className="credits-popover-text">
                        <div className="title">
                          Credits shown are estimated. It may vary based upon
                          token consumption.
                        </div>
                        <a
                          href="https://docs.saleshandy.com/en/articles/12412342-a-complete-guide-to-saleshandy-ai-credits"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="learn-more"
                        >
                          Learn More
                        </a>
                      </div>
                      <div className="cross-icon-container">
                        <Cross
                          width={15}
                          height={15}
                          className="cross-icon"
                          {...accessibleOnClick(() =>
                            setIsCreditsPopoverOpen(false),
                          )}
                        />
                      </div>
                    </div>
                  }
                  placement={Placement.Top}
                  show={isCreditsPopoverOpen}
                  onToggle={setIsCreditsPopoverOpen}
                  rootClose={true}
                >
                  <AiCreditCapsule
                    credits={selectedModalAiCreditUsagePerRow || '1-3'}
                  />
                </OverlayPopover>
              </div>
              <Select
                options={AiOptionList}
                optionRenderer={(option) => (
                  <div className="ai-credits">
                    <div className="ai-modal-icon">
                      <ImageIcon
                        src={option?.icon}
                        alt={option?.label}
                        width={16}
                        height={16}
                      />
                    </div>
                    <div className="ai-modal-content">
                      <div className="ai-modal-credits">
                        <div className="modal-recommended">
                          <span
                            className={`${
                              option?.key ===
                              String(getTemplateById?.suggestedAiModel?.id)
                                ? 'recommended-modal'
                                : 'ai-modal-label'
                            }`}
                          >
                            <TruncatedTextWithAutoTooltip
                              text={option?.label}
                              placement={Placement.Left}
                            />
                          </span>
                          {option?.key ===
                            String(getTemplateById?.suggestedAiModel?.id) && (
                            <span className="recommended">Recommended</span>
                          )}
                        </div>
                        <AiCreditCapsule
                          credits={
                            getAiModelCreditUsagePerRow(option?.key) || '1-3'
                          }
                          showBorder
                        />
                      </div>
                      <div className="ai-modal-description">
                        {option?.description ??
                          'Advanced personalization and research. Best for tailored emails and smart enrichment.'}
                      </div>
                    </div>
                  </div>
                )}
                selectedOptionKey={enrichPromptData?.aiModelId}
                selectedOptionRenderer={([option]) => (
                  <span className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center">
                      <ImageIcon
                        src={option?.icon}
                        alt={option?.label}
                        className="pr-2"
                      />
                      <span>{option?.label}</span>
                    </div>
                    {enrichPromptData?.isWebSearchEnabled && (
                      <ImageIcon src={Images?.GlobeBlue} alt="globe" />
                    )}
                  </span>
                )}
                onChange={handleSelectChange('aiModelId')}
                className="select-ai-options"
                showSearch={true}
                showOptionsSeparator={true}
                filterOption={(value, option) =>
                  option?.label?.toLowerCase().includes(value.toLowerCase())
                }
                customHeader={
                  <>
                    <div className="ai-modal-header">
                      <div className="globe-icon-container">
                        <Globes className="globe-icon" />
                      </div>
                      <span className="ai-modal-header-title">Web Search </span>
                      <OverlayTooltip text="Enable web search so AI can access the internet for useful and updated info.">
                        <div className="info-circle-container">
                          <InfoCircle className="header-info-circle-icon" />
                        </div>
                      </OverlayTooltip>
                    </div>
                    <div className="ai-modal-switch">
                      <Switch
                        checked={!!enrichPromptData?.isWebSearchEnabled}
                        onChange={handleWebSearchToggle}
                        size={Size.Small}
                      />
                    </div>
                  </>
                }
                placement={Placement.Bottom}
              />
            </div>

            <div className="enrich-own-prompt-content-container">
              <div className="enrich-own-prompt-label-container prompt-merge-tag-editor">
                <div className="enrich-own-prompt-label d-flex align-items-center gap-1">
                  <Label>Enter Instructions</Label>
                  <OverlayPopover
                    className="help-popover"
                    show={isTextPopoverOpen}
                    onToggle={setIsTextPopoverOpen}
                    content={
                      <div className="help-popover-content">
                        <div className="help-popover-text">
                          <div className="title">
                            Describe how you want AI to enrich data. Personalize
                            with merge tags.
                          </div>
                          <a
                            href="https://docs.saleshandy.com/en/articles/12290754-how-to-use-ai-prospect-enrichment-to-run-hyper-personalized-campaigns"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="learn-more"
                          >
                            Learn More
                          </a>
                        </div>
                        <div className="cross-icon-container">
                          <Cross
                            width={15}
                            height={15}
                            className="cross-icon"
                            {...accessibleOnClick(() =>
                              setIsTextPopoverOpen(false),
                            )}
                          />
                        </div>
                      </div>
                    }
                    placement={Placement.Bottom}
                    rootClose={true}
                  >
                    <Help className="help-logo" />
                  </OverlayPopover>
                </div>
                <div>{renderMergeTagSelection()}</div>
              </div>
              <Editor
                ref={contentRef}
                value={enrichPromptData?.instructions}
                onAiColEnrichmentEditorClick={() => {
                  // Fire the same event as outside click - trigger the mousedown event
                  const event = new MouseEvent('mousedown', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                  });
                  document.body.click();
                  document.body.dispatchEvent(event);
                }}
                onEditorChange={(value: string) => {
                  setEnrichPromptData((prev) => {
                    const newValidation = validateMergeTags(
                      value,
                      allowedMergeTags,
                    );
                    return {
                      ...prev,
                      instructions: value,
                      mergeTags: newValidation?.validTags,
                    };
                  });
                }}
                addMergeTagInContent={handleTagSelect}
                mergeTags={mergeTagOptions}
                getEditorInstance={(editor) => {
                  inMailEditorRef.current = editor;
                }}
                showMergeAndVariableTags={true}
                showAddAttachments={false}
                disabledVariableTags={['SenderFirstName']}
                className="enrich-prompt-merge-tag-editor"
                maxInputCount={5000}
                error={error.error}
                errorMessage={error.message}
                // isInline={false}
                isToolbardynamic={false}
                showVariableTag={false}
                isCustomClass={true}
                showDefaultContentStyle
                placeholder="Type your enrichment prompt using merge tags e.g., describe {{Domain}} in a sentence."
                showCount
                showContentStyle
                disableImagePaste={true}
                treatMergeTagAsVariableTag
              />

              <div className="enrich-own-prompt-label-container">
                <ImageIcon src={Images?.Notes} alt="help-icon" />
                <span className="browse-template-container">
                  <span
                    className="browse-template"
                    {...accessibleOnClick(handleBrowseTemplateClick)}
                  >
                    Browse Template
                  </span>
                  {/* This is for browse template discard modal */}
                  <DiscardUnsavedChangesModal
                    show={isDiscardModalOpen}
                    onHide={closeDiscardModal}
                    onDiscard={() => {
                      handleMoveToNextStep(
                        AiColEnrichmentModalSteps.TEMPLATE_LIST,
                      );
                    }}
                    text="Are you sure you want to choose a template without saving? Any unsaved changes will be lost."
                  />
                  <span className="enrich-prospect-with-ai">
                    {' '}
                    to Enrich Prospect with AI.
                  </span>
                </span>
              </div>
            </div>

            {/* Format Data */}
            <div className="enrich-own-prompt-content-container custom-output-format">
              <div className="enrich-own-prompt-label-container">
                <Label className="enrich-own-prompt-label">Output Format</Label>
                <OverlayTooltip text="Choose how results appear, use a pre-set format or Instruct AI with your custom instructions.">
                  <InfoCircle className="header-info-circle-icon" />
                </OverlayTooltip>
              </div>
              <Select
                placeholder="Select Output Format"
                options={formatOptions(
                  getTemplateById,
                  currentStep,
                  getAiModelList,
                  enrichPromptData,
                )}
                placement={Placement.Auto}
                popperConfig={{
                  modifiers: [
                    {
                      name: 'flip',
                      options: {
                        fallbackPlacements: ['bottom'],
                        allowedAutoPlacements: ['top', 'bottom'],
                      },
                    },
                  ],
                }}
                optionRenderer={(option: any) => (
                  <OverlayTooltip
                    text={option?.text}
                    placement={Placement.Left}
                    show={activeTooltip === option?.label}
                  >
                    <span
                      className="d-flex align-items-center w-100"
                      onMouseEnter={() => setActiveTooltip(option?.label)}
                      onMouseLeave={() => setActiveTooltip(null)}
                    >
                      <ImageIcon
                        src={option?.icon}
                        alt={option?.label}
                        className="pr-2"
                      />
                      <span>{option?.label}</span>
                    </span>
                  </OverlayTooltip>
                )}
                selectedOptionKey={enrichPromptData?.selectedOutputFormat}
                selectedOptionRenderer={([option]: any[]) => (
                  <OverlayTooltip text={option?.text}>
                    <span>
                      <ImageIcon
                        src={option?.icon}
                        alt={option?.label}
                        className="pr-2"
                      />
                      <span>{option?.label}</span>
                    </span>
                  </OverlayTooltip>
                )}
                onChange={handleSelectChange('selectedOutputFormat')}
                className="select-output-format"
              />

              {enrichPromptData.selectedOutputFormat === 'custom' && (
                <>
                  <div
                    className={`enrich-own-prompt-custom-format ${
                      enrichPromptData?.customOutputFormatInput?.length >
                        2500 ||
                      (enrichPromptData?.customOutputFormatInput?.length < 10 &&
                        enrichPromptData?.customOutputFormatInput?.length >= 1)
                        ? 'input-error'
                        : ''
                    }`}
                  >
                    <Input
                      type="textarea"
                      placeholder="Please enter your prompt here..."
                      className="custom-output-format-input"
                      value={enrichPromptData.customOutputFormatInput}
                      onChange={(e) => {
                        const { value } = e.target;
                        setEnrichPromptData((prev) => ({
                          ...prev,
                          customOutputFormatInput: value,
                        }));
                      }}
                    />

                    <div className="enrich-own-prompt-custom-format-footer">
                      <div className="enrich-prompt-custom-format-type">
                        <span className="custom-format-text">Column Type</span>
                        <OverlayPopover
                          show={customOutputFormat}
                          onToggle={setCustomOutputFormat}
                          content={
                            <div className="custom-output-dropdown-select">
                              {Object.values(
                                SupportedAiModelsOutputColumnType,
                              ).map((type) => (
                                <div
                                  key={type}
                                  className={`
                                custom-output-dropdown-select-single-content
                                ${
                                  enrichPromptData.selectedType === type
                                    ? 'active'
                                    : ''
                                }
                                `}
                                  {...accessibleOnClick(() => {
                                    setEnrichPromptData((prev) => ({
                                      ...prev,
                                      selectedType: type,
                                    }));
                                    setCustomOutputFormat(false);
                                  })}
                                >
                                  {OutputTypeLabels[type]}
                                </div>
                              ))}
                            </div>
                          }
                          placement={Placement.Top}
                          className="custom-output-dropdown-select-container"
                          rootClose={true}
                        >
                          <Button
                            className="custom-output-dropdown"
                            {...accessibleOnClick(() => {
                              setCustomOutputFormat(!customOutputFormat);
                            })}
                            disabled={!enrichPromptData?.isNewColumnName}
                          >
                            <span>
                              {OutputTypeLabels[enrichPromptData.selectedType]}{' '}
                              {customOutputFormat ? (
                                <ChevronUp />
                              ) : (
                                <ChevronDown />
                              )}
                            </span>
                          </Button>
                        </OverlayPopover>
                      </div>
                      <div className="simple-input-count-container">
                        <span className="simple-input-count">
                          {enrichPromptData?.customOutputFormatInput?.length ??
                            0}
                          /{2500}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className="custom-output-format-error">
                    {enrichPromptData?.customOutputFormatInput?.length > 2500
                      ? 'Character limit exceeded'
                      : enrichPromptData?.customOutputFormatInput?.length <
                          10 &&
                        enrichPromptData?.customOutputFormatInput?.length >= 1
                      ? 'Minimum 10 character required'
                      : ''}
                  </span>
                </>
              )}
            </div>

            {/* Run Settings */}
            <div className="enrich-own-prompt-content-container">
              <div className="enrich-own-prompt-label-container">
                <Label className="enrich-own-prompt-label">Run Settings</Label>
              </div>
              <div className="run-settings-content">
                {/* Run Automatically */}
                <div className="run-settings-row-container">
                  <div className="run-settings-row">
                    <div className="run-settings-label-container">
                      <span className="run-settings-label">
                        Run Automatically
                      </span>
                      <OverlayTooltip text="Runs automatically whenever a new prospect is added, if the conditions you set are met. ">
                        <InfoCircle className="run-settings-info-icon" />
                      </OverlayTooltip>
                    </div>
                    <div className="run-settings-switch">
                      <Switch
                        checked={enrichPromptData?.isAutoRunEnabled}
                        onChange={handleAutoRunChange}
                        size={Size.Small}
                      />
                    </div>
                  </div>
                  <p className="run-settings-description">
                    Executes automatically upon adding new prospects.
                  </p>
                </div>

                {/* Only Run If */}
                <div className="run-settings-row-container">
                  <div className="run-settings-row">
                    <div className="run-settings-label-container">
                      <span className="run-settings-label">
                        Run If Condition
                      </span>
                      <OverlayPopover
                        className="help-popover"
                        show={isRunSettingsPopoverOpen}
                        onToggle={setIsRunSettingsPopoverOpen}
                        content={
                          <div className="help-popover-content">
                            <div className="help-popover-text">
                              <div className="title">
                                Runs only when the specified conditions are met.
                              </div>
                              <a
                                href="https://docs.saleshandy.com/en/articles/12290754-how-to-use-ai-prospect-enrichment-to-run-hyper-personalized-campaigns"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="learn-more"
                              >
                                Learn More
                              </a>
                            </div>
                            <div className="cross-icon-container">
                              <Cross
                                width={15}
                                height={15}
                                className="cross-icon"
                                {...accessibleOnClick(() =>
                                  setIsRunSettingsPopoverOpen(false),
                                )}
                              />
                            </div>
                          </div>
                        }
                        placement={Placement.Bottom}
                        rootClose={true}
                      >
                        <Help className="help-logo" />
                      </OverlayPopover>
                    </div>
                    <div className="run-settings-control">
                      {isAutoRunConditionApplied && (
                        <div className="applied-conditions-chips">
                          <Pills
                            theme="outline"
                            size="md"
                            label={`${
                              autoRunConditionFilterGroups?.length || 0
                            } Applied`}
                            className="applied-conditions-chip"
                          />
                        </div>
                      )}
                      <Button
                        className="set-condition-button"
                        {...accessibleOnClick(handleSetConditionClick)}
                      >
                        {isAutoRunConditionApplied ? (
                          <span className="set-condition-button-content">
                            <EditBlue width={16} height={16} />
                            <span>Edit Conditions</span>
                          </span>
                        ) : (
                          <span className="set-condition-button-content">
                            <PlusBlue width={16} height={16} />
                            <span>Set Conditions</span>
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                  <p className="run-settings-description last-description">
                    Conditions defined here will apply every time this column
                    runs, whether triggered automatically or manually.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        <TemplateFooter
          saveAndDontRun={handleSaveBtnClicked}
          templateId={getTemplateById?.id}
          disableFooter={
            getTemplateByIdRequestStatus === RequestStatus.Pending ||
            previewModalOpen
          }
          footerDisabled={
            enrichPromptData?.instructions?.length === 0 ||
            error.error ||
            (enrichPromptData.selectedOutputFormat === 'custom' &&
              (enrichPromptData.customOutputFormatInput?.length < 10 ||
                enrichPromptData.customOutputFormatInput?.length > 2500))
          }
          enrichPromptData={enrichPromptData}
          templateData={getTemplateById}
          setPreviewModalOpen={setPreviewModalOpen}
          currentStep={currentStep}
          selectedModalAiCreditUsagePerRow={
            Number(selectedModalAiCreditUsagePerRow) || 3 // Fallback to 3 if no value is available or API returned fallback "1-3"
          }
          bulkProspectSelected={bulkProspectSelected}
          resetBulkProspectSelected={resetBulkProspectSelected}
          isSaveDisabledForColumnName={
            !enrichPromptData?.isNewColumnName &&
            enrichPromptData?.fieldId?.length === 0
          }
          isNewColumnName={enrichPromptData?.isNewColumnName}
        />

        {showRunSettingsConditionModal && (
          <ProspectFiltersModal
            show={showRunSettingsConditionModal}
            onClose={() => {
              setShowRunSettingsConditionModal(false);
            }}
            headerTitle={
              isColumnLocked
                ? lockedLabel
                  ? `Conditions for ${lockedLabel}`
                  : `Conditions`
                : enrichPromptData?.columnName
                ? `Conditions for ${enrichPromptData?.columnName}`
                : `Conditions`
            }
            onApplyAiColEnrichmentAutoRunSettingsCondition={(
              filterGroups: FilterGroup[],
              autoRunCondition: any,
              autoRunConditionLogic: any,
            ) => {
              setAutoRunConditionFilterGroups(filterGroups);
              setAutoRunConditionFilterGroupsLogic(autoRunConditionLogic);
              setEnrichPromptData((prev) => ({
                ...prev,
                autoRunConditions: autoRunCondition,
              }));
            }}
          />
        )}
      </>
    );
  },
);

export default AiColTemplateDetail;
